import { useState } from "react";
import { Phone, Mail, MapPin, Globe, Wrench, Edit2, Trash2, Send } from "lucide-react";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import ConfirmDialog from "../components/ConfirmDialog";
import HistoryTimeline from "../components/HistoryTimeline";
import { corrigirContato } from "../lib/contatoEdit";
import { PARTNER_STAGES, PARTNER_TYPES, BRAZILIAN_STATES } from "../constants";
import { precisaJustificar, justificativaVigente, diasParado, rotuloMotivo, contatosSemComprovante } from "../lib/abordagem";
import { fmtDate, fmtShort, getToday, addMonths, credStatus, maskCEP, fetchCEP } from "../utils";
import Field, { inputStyle, selectStyle } from "../components/Field";

// Fluxo principal de credenciamento (exclui estágios fora do funil)
const PARTNER_FLOW = ["identificado", "abordagem", "documentacao", "validacao", "ativacao", "ativo"];

export default function PartnerDetail({
  partner, onClose, setPartners,
  movePartner, setShowNewInteraction,
  onDelete,
  credenciamentos = [], onSendLink, onEditFicha, userName = "",
}) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [cepLoading, setCepLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [noInterestOpen, setNoInterestOpen] = useState(false);
  const [resumeDate, setResumeDate] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erroMotivo, setErroMotivo] = useState("");

  if (!partner) return null;

  const type = PARTNER_TYPES.find((t) => t.id === partner.type);
  const stage = PARTNER_STAGES.find((s) => s.id === partner.stage);

  const startEdit = () => {
    setEditData({
      name: partner.name, type: partner.type, contact: partner.contact,
      phone: partner.phone, email: partner.email || "",
      region: partner.region, address: partner.address || "",
      cep: partner.cep || "", cidade: partner.cidade || "", bairro: partner.bairro || "",
      services: partner.services || "", notes: partner.notes || "",
    });
    setEditing(true);
  };

  // Mesma busca do cadastro novo: quem edita a ficha na rua também não deve
  // digitar endereço à mão.
  const handleCEP = async (val) => {
    const masked = maskCEP(val);
    setEditData((p) => ({ ...p, cep: masked }));
    if (masked.replace(/\D/g, "").length !== 8) return;
    setCepLoading(true);
    const r = await fetchCEP(masked);
    setCepLoading(false);
    if (!r) return;
    setEditData((p) => ({
      ...p,
      address: [r.rua, r.bairro].filter(Boolean).join(", ") || p.address,
      bairro: r.bairro || p.bairro,
      cidade: r.cidade || p.cidade,
      region: r.uf || p.region,
    }));
  };

  const saveEdit = () => {
    setPartners((prev) => prev.map((p) =>
      p.id === partner.id ? { ...p, ...editData } : p
    ));
    setEditing(false);
    onClose();
  };

  // Move o parceiro para um estágio, registrando histórico e campos extras.
  // `trilha` é o dado estruturado do evento (tipo, motivo): ele mora dentro do
  // histórico porque campo solto sem coluna é descartado ao salvar.
  const moveToStage = (next, note, extra = {}, trilha = {}) => {
    setPartners((prev) => prev.map((p) =>
      p.id === partner.id ? {
        ...p, stage: next, ...extra,
        stageHistory: [...(p.stageHistory || []), { from: p.stage, to: next, date: getToday(), note, ...trilha }],
      } : p
    ));
    onClose();
  };

  // Aceite na abordagem → abre o registro de contato já marcado em "Aceitou
  // credenciar". Mover direto para Documentação pulava a prova (export da
  // conversa ou registro telefônico) que a Rede exige para avançar.
  const acceptCredential = () => {
    onClose();
    setShowNewInteraction({ type: "partner", id: partner.id, resultado: "aceitou" });
  };

  // Sem interesse agora → sai do fluxo e agenda retomada na data escolhida.
  // O motivo é obrigatório: sem ele, a retomada daqui a três meses começa do
  // zero, e o "não" de hoje não ensina nada a ninguém.
  const markNoInterest = (date, motivo) => moveToStage("sem_interesse", `Sem interesse: ${motivo}`, {
    nextAction: "Retomar contato (sem interesse anterior)",
    nextActionDate: date || addMonths(getToday(), 3),
  }, { tipo: "sem_interesse", motivo });

  const openNoInterest = () => { setResumeDate(addMonths(getToday(), 3)); setMotivo(""); setErroMotivo(""); setNoInterestOpen(true); };

  // Retomar um parceiro que estava sem interesse → volta para Abordagem
  const resumeContact = () => moveToStage("abordagem", "Contato retomado", {
    nextAction: "", nextActionDate: "",
  }, { tipo: "retomada" });

  const inFlow = PARTNER_FLOW.includes(partner.stage);

  return (
    <>
      <Modal open={!!partner} onClose={onClose} title="Detalhe do Parceiro" w={580}>
        {editing ? (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <Field label="Nome"><input style={inputStyle} value={editData.name} onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))} /></Field>
              </div>
              <Field label="Tipo">
                <select style={selectStyle} value={editData.type} onChange={(e) => setEditData((p) => ({ ...p, type: e.target.value }))}>
                  {PARTNER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Contato"><input style={inputStyle} value={editData.contact} onChange={(e) => setEditData((p) => ({ ...p, contact: e.target.value }))} /></Field>
              <Field label="Telefone"><input style={inputStyle} value={editData.phone} onChange={(e) => setEditData((p) => ({ ...p, phone: e.target.value }))} /></Field>
              <Field label="E-mail"><input style={inputStyle} value={editData.email} onChange={(e) => setEditData((p) => ({ ...p, email: e.target.value }))} /></Field>
              <Field label="CEP" help={cepLoading ? "Buscando endereço..." : "Preenche endereço, bairro, cidade e UF"}>
                <input style={inputStyle} value={editData.cep || ""} onChange={(e) => handleCEP(e.target.value)} placeholder="00000-000" inputMode="numeric" />
              </Field>
              <Field label="Cidade"><input style={inputStyle} value={editData.cidade || ""} onChange={(e) => setEditData((p) => ({ ...p, cidade: e.target.value }))} /></Field>
              <Field label="Bairro"><input style={inputStyle} value={editData.bairro || ""} onChange={(e) => setEditData((p) => ({ ...p, bairro: e.target.value }))} /></Field>
              <div style={{ gridColumn: "1/-1" }}>
                <Field label="Endereço"><input style={inputStyle} value={editData.address} onChange={(e) => setEditData((p) => ({ ...p, address: e.target.value }))} placeholder="Rua, número" /></Field>
              </div>
              <Field label="Estado (UF)">
                <select style={selectStyle} value={editData.region} onChange={(e) => setEditData((p) => ({ ...p, region: e.target.value }))}>
                  {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf} — {s.name}</option>)}
                </select>
              </Field>
              <div style={{ gridColumn: "1/-1" }}>
                <Field label="Serviços"><textarea style={{ ...inputStyle, minHeight: 40, resize: "vertical" }} value={editData.services} onChange={(e) => setEditData((p) => ({ ...p, services: e.target.value }))} /></Field>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <Field label="Observações"><textarea style={{ ...inputStyle, minHeight: 40, resize: "vertical" }} value={editData.notes} onChange={(e) => setEditData((p) => ({ ...p, notes: e.target.value }))} /></Field>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 8 }}>
              <button onClick={() => setEditing(false)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
              <button onClick={saveEdit} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Salvar Alterações</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{partner.name}</div>
                <div style={{ fontSize: 13, color: "#64748b" }}>{type?.label} — {partner.contact}</div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Badge color={stage?.color}>{stage?.label}</Badge>
                <button onClick={startEdit} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 2 }} title="Editar"><Edit2 size={14} /></button>
                <button onClick={() => setShowDeleteConfirm(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 2 }} title="Excluir"><Trash2 size={14} /></button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
              {[
                { icon: <Phone size={12} />, text: partner.phone },
                { icon: <Mail size={12} />, text: partner.email || "—" },
                { icon: <MapPin size={12} />, text: partner.address },
                { icon: <Globe size={12} />, text: (BRAZILIAN_STATES.find((s) => s.uf === partner.region) ? `${partner.region} — ${BRAZILIAN_STATES.find((s) => s.uf === partner.region).name}` : partner.region) },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 6, background: "#f8fafc", fontSize: 11, color: "#475569" }}>
                  <span style={{ color: "#7c3aed" }}>{item.icon}</span>{item.text}
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, color: "#475569", marginBottom: 8 }}>
              <Wrench size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />Serviços: {partner.services}
            </div>

            {partner.docs?.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Documentação:</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {partner.docs.map((d) => <Badge key={d} color="#059669" variant="outline">{d}</Badge>)}
                </div>
              </div>
            )}

            {/* Credenciamento (fichas ligadas a este parceiro) */}
            {(onSendLink || onEditFicha) && (
              <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, background: "#faf5ff", border: "1px solid #e9d5ff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#6d28d9" }}>Credenciamento</span>
                  {onSendLink && (
                    <button onClick={() => onSendLink(partner)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                      <Send size={12} /> Enviar formulário
                    </button>
                  )}
                </div>
                {(() => {
                  const fichas = (credenciamentos || []).filter((c) => c.partnerId === partner.id);
                  if (!fichas.length) return <div style={{ fontSize: 11, color: "#94a3b8" }}>Nenhuma ficha ainda. Envie o link ou crie pela aba Fichas.</div>;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {fichas.map((f) => {
                        const st = credStatus(f);
                        return (
                          <div key={f.id} onClick={() => onEditFicha && onEditFicha(f)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 10px", borderRadius: 7, background: "#fff", border: "1px solid #e9d5ff", cursor: onEditFicha ? "pointer" : "default" }}>
                            <span style={{ fontSize: 12, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {f.nomeFantasia || f.razaoSocial || f.responsavelNome || "Ficha"}{f.origem === "link" ? " · via link" : ""}
                            </span>
                            <Badge color={st.color} small>{st.label}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Situação da abordagem: silêncio cobrado ou espera justificada.
                O detalhe é onde o gerente vem olhar — precisa contar isso. */}
            {precisaJustificar(partner) && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 11.5, color: "#991b1b", lineHeight: 1.45, marginBottom: 14 }}>
                <strong>Parado há {diasParado(partner)} dias em abordagem</strong> sem contato registrado.
                Registre o contato (com o print) ou justifique a espera pela aba Rede de Parceiros.
              </div>
            )}
            {!precisaJustificar(partner) && justificativaVigente(partner) && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 11.5, color: "#92400e", lineHeight: 1.45, marginBottom: 14 }}>
                Aguardando resposta do parceiro — {rotuloMotivo(justificativaVigente(partner).motivo)}
                {justificativaVigente(partner).detalhe ? `: ${justificativaVigente(partner).detalhe}` : ""}.
                Nova tentativa em <strong>{fmtShort(justificativaVigente(partner).proximaTentativa)}</strong>
                {justificativaVigente(partner).autor ? ` (justificado por ${justificativaVigente(partner).autor})` : ""}.
              </div>
            )}
            {contatosSemComprovante(partner).length > 0 && (
              <div style={{ padding: "8px 12px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: 11, color: "#64748b", marginBottom: 14 }}>
                {contatosSemComprovante(partner).length} {contatosSemComprovante(partner).length === 1 ? "contato registrado" : "contatos registrados"} sem print anexado.
              </div>
            )}

            {partner.notes && (
              <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#78350f", lineHeight: 1.4, marginBottom: 14 }}>
                {partner.notes}
              </div>
            )}

            {/* Histórico unificado (estágios + contatos) */}
            <div style={{ marginBottom: 14 }}>
              <HistoryTimeline
                stageHistory={partner.stageHistory} interactions={partner.interactions} stageList={PARTNER_STAGES}
                ehParceiro
                onEditContato={(ref, patch) => setPartners((prev) => prev.map((p2) => (p2.id === partner.id ? corrigirContato(p2, ref, patch, userName) : p2)))}
              />
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => setShowNewInteraction({ type: "partner", id: partner.id })} style={{ padding: "7px 12px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                + Registrar Contato
              </button>

              {/* Aceite na abordagem → Documentação */}
              {partner.stage === "abordagem" && (
                <button onClick={acceptCredential} style={{ padding: "7px 12px", borderRadius: 6, border: "none", background: "#0d9488", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  Aceitou — ir para Documentação
                </button>
              )}

              {/* NÃO EXISTE MAIS "AVANÇAR ESTÁGIO".
                  O botão movia a empresa de estágio sem nada ter acontecido:
                  dava para marcar "abordagem" sem ter falado com ninguém, e o
                  funil passava a contar conversas que não existiram. O estágio
                  agora é consequência do ato registrado — contato, aceite ou
                  recusa (ver ACOES_REDE em src/constants). */}

              {/* Sem interesse → sai do fluxo, retomar depois */}
              {inFlow && (
                <button onClick={openNoInterest} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #fcd34d", background: "#fffbeb", color: "#b45309", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  Sem interesse (retomar depois)
                </button>
              )}

              {/* Retomar parceiro que estava sem interesse */}
              {partner.stage === "sem_interesse" && (
                <button onClick={resumeContact} style={{ padding: "7px 12px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                  Retomar contato → Abordagem
                </button>
              )}
            </div>

            {/* Painel: escolher data de retomada ao marcar sem interesse */}
            {noInterestOpen && (
              <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Por que não seguiu?</div>
                <input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: já atende por outra rede, achou a taxa alta, vai avaliar em 2026"
                  style={{ ...inputStyle, width: "100%", marginBottom: erroMotivo ? 2 : 10, borderColor: erroMotivo ? "#ef4444" : undefined }}
                />
                {erroMotivo && <div style={{ fontSize: 10.5, color: "#dc2626", marginBottom: 8 }}>{erroMotivo}</div>}
                <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Quando retomar o contato?</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                  <input style={{ ...inputStyle, width: "auto" }} type="date" value={resumeDate} onChange={(e) => setResumeDate(e.target.value)} />
                  {[1, 2, 3, 6].map((m) => (
                    <button key={m} onClick={() => setResumeDate(addMonths(getToday(), m))} style={{
                      padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff",
                      color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer",
                    }}>
                      {m} {m === 1 ? "mês" : "meses"}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => {
                      if (!motivo.trim()) { setErroMotivo("Diga o motivo — é o que vai orientar a retomada."); return; }
                      markNoInterest(resumeDate, motivo.trim());
                      setNoInterestOpen(false);
                    }}
                    style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#d97706", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                  >
                    Confirmar sem interesse
                  </button>
                  <button onClick={() => setNoInterestOpen(false)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Aviso de retomada agendada */}
            {partner.stage === "sem_interesse" && partner.nextActionDate && (
              <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 11, color: "#92400e" }}>
                Sem interesse no momento — retomada agendada para <strong>{fmtShort(partner.nextActionDate)}</strong> (aparecerá no Painel do Dia nessa data).
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => { onDelete(partner.id); onClose(); }}
        title="Excluir Parceiro"
        message={`Tem certeza que deseja excluir "${partner.name}"? Essa ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
      />
    </>
  );
}
